import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { SegmentoVisao } from '@/contexts/SegmentoVisaoContext';

/**
 * FATURAS · 180 DIAS (Douglas 24/09/2026). As etapas vêm do backend
 * (domain/qualidade/faturas-mes.ts); os rótulos abaixo são a MESMA lista, e um
 * teste do backend confere que as duas não se separam.
 */
export type EtapaFatura =
  | 'venceu_sem_conta' | 'esperando_conta' | 'sem_linha' | 'pedida_bee' | 'bee_nao_achou' | 'conta_nao_saiu' | 'baixar_solar' | 'pedir_gerente' | 'pedida_gerente' | 'nao_entregue'
  | 'a_vencer' | 'validar_solar' | 'nao_pago' | 'prometeu' | 'possivel_estorno' | 'pago';

export const ETAPAS_CONTA: EtapaFatura[] = ['venceu_sem_conta', 'esperando_conta', 'sem_linha', 'pedida_bee', 'bee_nao_achou', 'conta_nao_saiu', 'baixar_solar', 'pedir_gerente', 'pedida_gerente', 'nao_entregue'];
export const ETAPAS_PAGAMENTO: EtapaFatura[] = ['a_vencer', 'validar_solar', 'nao_pago', 'prometeu', 'possivel_estorno', 'pago'];

export const ROTULO_ETAPA_FATURA: Record<EtapaFatura, string> = {
  venceu_sem_conta: 'Venceu sem conta',
  esperando_conta: 'Esperando a conta',
  sem_linha: 'Falta a linha',
  pedida_bee: 'Pedida ao Bee',
  bee_nao_achou: 'Bee recusou',
  conta_nao_saiu: 'Conta não saiu',
  baixar_solar: 'Baixar no SOLAR',
  pedir_gerente: 'Pedir à gerente',
  pedida_gerente: 'Pedida à gerente',
  nao_entregue: 'Conta não entregue',
  a_vencer: 'A vencer',
  validar_solar: 'Validar no SOLAR',
  nao_pago: 'Não pago',
  prometeu: 'Prometeu pagar',
  possivel_estorno: 'Possível estorno',
  pago: 'Pago',
};

/** Etapa que é dívida (bordô cheio). Espera sem culpa de ninguém fica neutra. */
export const ETAPA_DIVIDA = new Set<EtapaFatura>(['venceu_sem_conta', 'sem_linha', 'bee_nao_achou', 'baixar_solar', 'pedir_gerente', 'nao_entregue', 'validar_solar', 'nao_pago', 'possivel_estorno']);

export interface ContaAnterior { id: string; numero: number; vencimento: string | null; pagamento: 'pago' | 'nao_pago' | null }

export interface LinhaFaturaMes {
  id: string; pedido_id: string; numero: number; tipo: 'movel' | 'fixa'; safra: string | null;
  fatura_id: string | null; vencimento: string | null; vencimento_previsto: string | null;
  pagamento: 'pago' | 'nao_pago' | null; prometeu_pagar_ate: string | null; possivel_estorno: boolean;
  cobrado_em: string | null; cobrancas: number; entregue_whatsapp_em: string | null; entregue_email_em: string | null;
  email_destino: string | null; gerente_pedido_em: string | null; ultima_acao: string | null; ultima_acao_em: string | null;
  razao_social: string; documento: string; numero_pedido: string | null;
  consultor_nome: string | null; equipe_nome: string | null; valor: number; linhas: number;
  contas_em_aberto: number; valor_conta: number | null; etapa: EtapaFatura;
  /** Período da conta que chegou: menos de 28 dias é a conta proporcional. */
  conta_inicio?: string | null; conta_fim?: string | null;
  bee_situacao: string | null; bee_status?: string | null; pedido_bee_em: string | null; pedir_a_partir_de: string | null; sem_linha: boolean;
  conversa_id?: string | null; anteriores?: ContaAnterior[]; email_gestor?: string | null;
  /** Por que a conta não saiu, em poucas palavras (domain/qualidade/faturas-mes.ts). */
  motivo_entrega?: string | null;
  /** O que o Bee já tentou: a linha da vez e quantas linhas da conta ele recusou. */
  conta_resposta?: string | null; linha_bee?: string | null; bee_linhas?: number; bee_recusadas?: number; bee_esgotou?: boolean;
}

/** Para onde vai a conta: a linha do Bee e as tentadas, o WhatsApp e o e-mail. */
export interface DestinoConta {
  linha_bee: string | null;
  linhas: { linha: string; origem: 'confirmada' | 'pedido' | 'cadastro' | 'contato'; recusada: boolean; ultima: string | null }[];
  /** O pedido que está com o Bee agora e as linhas que ele está testando. */
  testando: { desde: string; linhas: string[] } | null;
  whatsapp: string | null; whatsapp_origem: string | null; email: string | null; cliente_ja_tem: boolean;
}

export interface ResumoTriagemFaturas {
  linhas: LinhaFaturaMes[]; total: number; hoje: string; vencem7: number;
  safras: { safra: string; pendentes: number }[];
  tipos: { movel: number; fixa: number };
  etapas: Partial<Record<EtapaFatura, number>>;
  contas: { conta: number; pagamento: number };
  /** Contas que o robô não move mais: são da analista (comAnalista, backend). */
  comAnalista?: number;
}

export interface GrupoFaturaMes {
  safra: string | null; consultorId: string | null; consultorNome: string | null; fotoUrl: string | null;
  equipeId: string | null; equipeNome: string | null; tipo: 'movel' | 'fixa'; numero: number; etapa: EtapaFatura;
  emAberto: boolean; baixada: boolean; existe: boolean; beeEsgotou?: boolean; pedidos: number; linhas: number; valor: number;
}

export async function requisitarFaturasMes<T>(caminho: string, body?: unknown): Promise<T> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Sua sessão expirou. Entre novamente.');
  const base = ((import.meta.env.VITE_SUPABASE_URL as string) || 'https://beta.crm-api.primetecia.com.br/api').replace(/\/$/, '');
  let r: Response;
  try {
    r = await fetch(`${base}/v1/qualidade/faturas-mes/${caminho}`, {
      method: body === undefined ? 'GET' : 'POST',
      headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch { throw new Error('Não foi possível conectar ao CRM. Nada foi alterado; tente de novo.'); }
  const corpo = await r.json().catch(() => ({ error: 'O CRM não respondeu corretamente. Tente de novo.' }));
  if (!r.ok) throw new Error(corpo.error || 'Não foi possível concluir. Tente de novo.');
  return corpo.data as T;
}

export const chaveFaturasMesVisao = (tipoPessoa: SegmentoVisao) => ['visao-prime-faturas-mes', tipoPessoa] as const;

export function useFaturasMesVisao(tipoPessoa: SegmentoVisao, habilitado = true) {
  return useQuery({
    queryKey: chaveFaturasMesVisao(tipoPessoa),
    enabled: habilitado,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    queryFn: () => requisitarFaturasMes<GrupoFaturaMes[]>(`visao-prime?tipoPessoa=${tipoPessoa}`),
  });
}

const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
export const rotuloSafra = (s?: string | null) => {
  if (!s) return 'sem mês';
  const [a, m] = s.split('-');
  return `${MESES[Number(m) - 1]}/${a.slice(2)}`;
};
export const mesCurto = (d?: string | null) => (d ? MESES[Number(d.slice(5, 7)) - 1] : '');

/**
 * A BAIXA DAS CONTAS DO MÊS (Douglas 24/09/2026) — a mesma conta na triagem e
 * na Visão Prime. "Conta" é o pedido (uma conta por pedido). % que falta =
 * faltam baixar ÷ contas que já existem; a que a Claro ainda não fechou fica
 * de fora do %, mas aparece.
 */
export interface BaixaDoMes { safra: string; contas: number; baixadas: number; existem: number; faltam: number; naoExistem: number; beeNaoConsegue: number; pctFalta: number | null }
export function baixaPorSafra(grupos: GrupoFaturaMes[], tipo: 'todos' | 'movel' | 'fixa'): BaixaDoMes[] {
  const mapa = new Map<string, BaixaDoMes>();
  for (const g of grupos) {
    if (!g.safra || (tipo !== 'todos' && g.tipo !== tipo)) continue;
    const b = mapa.get(g.safra) || { safra: g.safra, contas: 0, baixadas: 0, existem: 0, faltam: 0, naoExistem: 0, beeNaoConsegue: 0, pctFalta: null };
    b.contas += g.pedidos;
    if (g.baixada) b.baixadas += g.pedidos;
    if (g.existe) b.existem += g.pedidos; else b.naoExistem += g.pedidos;
    if (g.beeEsgotou) b.beeNaoConsegue += g.pedidos;
    mapa.set(g.safra, b);
  }
  return [...mapa.values()].map(b => {
    const faltam = b.existem - b.baixadas;
    return { ...b, faltam, pctFalta: b.existem > 0 ? faltam / b.existem : null };
  }).sort((a, b) => b.safra.localeCompare(a.safra));
}
